import React from 'react';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { createRun, listRuns, getRun, Run } from './api';
import { Upload, FileSpreadsheet, X, AlertCircle, Loader2 } from 'lucide-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import dayjs from 'dayjs';
import { cn } from 'lib/utils';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);

const PIPELINE = 'lhc_register';

const LhcRegisterRunsPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();

    // "As on" date of the LHC report being uploaded (its FY drives lineage enrichment).
    const [selectedDate, setSelectedDate] = React.useState('');
    const [file, setFile] = React.useState<File | null>(null);
    const [uploading, setUploading] = React.useState(false);
    const [uploadError, setUploadError] = React.useState('');
    const [uploadSuccess, setUploadSuccess] = React.useState('');

    const [runs, setRuns] = React.useState<Run[]>([]);
    const [error, setError] = React.useState<string>('');
    const [loading, setLoading] = React.useState(true);

    const [selectedErrorRun, setSelectedErrorRun] = React.useState<Run | null>(null);

    const fileInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Settings', href: '#' },
            { label: 'Update LHC Register' },
        ]);
    }, [setBreadcrumbs]);

    const fetchRuns = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await listRuns(PIPELINE);
            const sorted = [...data.runs].sort((a, b) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
            setRuns(sorted);
        } catch (err: any) {
            console.error('Failed to fetch runs:', err);
            setRuns([]);
            setError(err?.response?.data?.detail || err?.message || 'Failed to fetch runs');
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchRuns();
    }, [fetchRuns]);

    // Poll active runs until they settle (succeeded/failed).
    React.useEffect(() => {
        const activeRuns = runs.filter(run => run.status === 'running' || run.status === 'queued');
        if (activeRuns.length === 0) return;

        const poll = async () => {
            const updates = await Promise.all(
                activeRuns.map(async (run) => {
                    try {
                        const updatedRun = await getRun(run.id);
                        if (updatedRun.status !== run.status || updatedRun.state !== run.state) {
                            return updatedRun;
                        }
                    } catch (e) {
                        console.error(`Failed to poll run ${run.id}`, e);
                    }
                    return null;
                })
            );

            const runsToUpdate = updates.filter((r): r is Run => r !== null);
            if (runsToUpdate.length > 0) {
                setRuns(prevRuns => prevRuns.map(run => {
                    const updated = runsToUpdate.find(u => u.id === run.id);
                    return updated || run;
                }));
            }
        };

        const intervalId = setInterval(poll, 1000);
        return () => clearInterval(intervalId);
    }, [runs]);

    const getFinancialYear = (date: Date): string => {
        const month = date.getMonth(); // 0-indexed (0 = Jan, 3 = April)
        const year = date.getFullYear();
        if (month >= 3) { // April onwards
            return `${year}-${year + 1}`;
        }
        return `${year - 1}-${year}`;
    };

    const handleRun = async () => {
        setUploadError('');
        setUploadSuccess('');

        if (!selectedDate) {
            setUploadError('Please select the "as on" date of this snapshot');
            return;
        }

        const todayStr = dayjs().format('YYYY-MM-DD');
        if (selectedDate > todayStr) {
            setUploadError('Date cannot be in the future');
            return;
        }

        if (!file) {
            setUploadError('Please select a file to upload');
            return;
        }

        setUploading(true);
        try {
            const dateObj = new Date(selectedDate);
            const fy = getFinancialYear(dateObj);

            // year/month/day are recorded on the run for display; the CN register
            // pipeline itself upserts the whole file regardless of period.
            const formData = new FormData();
            formData.append('pipeline', PIPELINE);
            formData.append('year', fy);
            formData.append('month', (dateObj.getMonth() + 1).toString());
            formData.append('day', dateObj.getDate().toString());
            formData.append('file', file);

            await createRun(formData);
            setUploadSuccess('Upload started — the LHC register will update once processing succeeds.');

            setSelectedDate('');
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            fetchRuns();
        } catch (error: any) {
            setUploadError(error.message || 'Failed to create run');
        } finally {
            setUploading(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            const validTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel'
            ];

            if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
                setUploadError('Please select a valid Excel file (.xlsx or .xls)');
                setFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
                return;
            }

            setFile(selectedFile);
            setUploadError('');
        }
    };

    const removeFile = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-6 sm:px-8 py-6">
            {/* Upload Section */}
            <div className="max-w-3xl">
                <Card className="border-border">
                    <CardHeader className="pb-3 px-6 pt-5">
                        <CardTitle className="text-lg font-semibold flex items-center gap-2">
                            <Upload className="h-5 w-5 text-muted-foreground" />
                            Update LHC Register
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-6 pb-6">
                        <div className="flex items-start gap-4">
                            <div className="flex-1 max-w-[200px]">
                                <Input
                                    type="date"
                                    value={selectedDate}
                                    max={dayjs().format('YYYY-MM-DD')}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="h-10"
                                    disabled={uploading}
                                />
                            </div>

                            <div className="flex-1">
                                {!file ? (
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className="h-10 border border-dashed border-input rounded-md flex items-center px-4 cursor-pointer hover:bg-accent/50 transition-colors text-sm text-muted-foreground"
                                    >
                                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                                        Select "LHC Report" Excel File...
                                    </div>
                                ) : (
                                    <div className="h-10 border border-dashed border-blue-200 bg-blue-50/50 rounded-md flex items-center justify-between px-3 text-sm">
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <FileSpreadsheet className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                            <span className="truncate font-medium text-blue-700">{file.name}</span>
                                        </div>
                                        <button
                                            onClick={removeFile}
                                            className="ml-2 p-1 hover:bg-blue-100 rounded-full text-blue-600 transition-colors"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    disabled={uploading}
                                />
                            </div>

                            <Button
                                onClick={handleRun}
                                disabled={uploading || !file || !selectedDate}
                                className="h-10 min-w-[100px]"
                            >
                                {uploading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                ) : (
                                    'Upload'
                                )}
                            </Button>
                        </div>

                        {uploadError && (
                            <p className="text-sm text-destructive mt-3">{uploadError}</p>
                        )}
                        {uploadSuccess && (
                            <p className="text-sm text-green-600 mt-3">{uploadSuccess}</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Previous Runs Section */}
            <div className="space-y-4">
                <h2 className="text-lg font-semibold tracking-tight">Previous Uploads</h2>
                <div className="rounded-md border bg-card">
                    {error ? (
                        <div className="p-4">
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-red-600 text-sm">{error}</p>
                            </div>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/50">
                                    <TableHead className="w-[180px]">As On</TableHead>
                                    <TableHead>Financial Year</TableHead>
                                    <TableHead>Uploaded By</TableHead>
                                    <TableHead>Uploaded At</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right w-[180px]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-24 text-center">
                                            <div className="flex items-center justify-center text-muted-foreground">
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mr-2" />
                                                Loading uploads...
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : runs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                                            No uploads found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    runs.map((run) => (
                                        <TableRow key={run.id}>
                                            <TableCell className="font-medium">
                                                {run.month ? `${dayjs().month(run.month - 1).format('MMMM')} ${run.day}` : '—'}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {run.year}
                                            </TableCell>
                                            <TableCell>
                                                {run.created_by}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {dayjs(run.created_at).format('MMM D, YYYY h:mm A')}
                                            </TableCell>
                                            <TableCell>
                                                <span className={cn(
                                                    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                                                    run.status === 'succeeded' ? "bg-green-50 text-green-700 ring-green-600/20" :
                                                        run.status === 'running' ? "bg-blue-50 text-blue-700 ring-blue-600/20" :
                                                            run.status === 'queued' ? "bg-yellow-50 text-yellow-700 ring-yellow-600/20" :
                                                                "bg-red-50 text-red-700 ring-red-600/20"
                                                )}>
                                                    {(run.status === 'running' || run.status === 'queued') && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
                                                    {run.status === 'succeeded' ? 'Succeeded' :
                                                        run.status === 'running' ? 'Processing' :
                                                            run.status === 'queued' ? 'Queued' :
                                                                'Failed'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2 min-h-[28px]">
                                                    {run.status === 'failed' && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 text-xs border-destructive text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5 px-2.5"
                                                            onClick={() => setSelectedErrorRun(run)}
                                                        >
                                                            <AlertCircle className="h-3.5 w-3.5" />
                                                            View Error Log
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>

            <Dialog open={!!selectedErrorRun} onOpenChange={(open) => !open && setSelectedErrorRun(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertCircle className="h-5 w-5" />
                            Upload Failed
                        </DialogTitle>
                        <DialogDescription>
                            Run ID: {selectedErrorRun?.id}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2 bg-muted/50 rounded-md p-4 max-h-[400px] overflow-y-auto">
                        <pre className="text-sm font-mono whitespace-pre-wrap text-foreground">
                            {selectedErrorRun?.error || 'No error details available.'}
                        </pre>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default LhcRegisterRunsPage;
